import React, { useState, useEffect } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  Box,
  BlockStack,
  InlineStack,
  TextField,
  Divider,
  Spinner,
  Thumbnail,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

const API_BASE = "/api";

export default function ShowroomLinkPage() {
  const { shopDomain } = useShop();
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState(false);

  const showroomUrl = shopDomain
    ? `${window.location.origin}/showroom?shop=${encodeURIComponent(shopDomain)}`
    : "";

  useEffect(() => {
    if (!shopDomain) return;
    setLoadingPreview(true);
    setPreviewError(false);
    fetch(`${API_BASE}/widget/showroom?shop=${encodeURIComponent(shopDomain)}`)
      .then((r) => r.json())
      .then((data) => setPreview(data.products || []))
      .catch(() => setPreviewError(true))
      .finally(() => setLoadingPreview(false));
  }, [shopDomain]);

  const copyUrl = () => {
    navigator.clipboard.writeText(showroomUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <Page title={t("showroomAdmin.title")} subtitle={t("showroomAdmin.subtitle")}>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                <Text variant="headingMd" as="h2">
                  {t("showroomAdmin.linkLabel")}
                </Text>
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <TextField
                      label=""
                      labelHidden
                      value={showroomUrl}
                      readOnly
                      autoComplete="off"
                    />
                  </div>
                  <Button onClick={copyUrl}>
                    {copied ? t("showroomAdmin.copied") : t("showroomAdmin.copy")}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => window.open(showroomUrl, "_blank")}
                    disabled={!showroomUrl}
                  >
                    {t("showroomAdmin.open")}
                  </Button>
                </InlineStack>

                <Divider />

                <Text variant="headingSm" as="h3">
                  {t("showroomAdmin.menuTitle")}
                </Text>
                <BlockStack gap="100">
                  <Text variant="bodyMd"><strong>1.</strong> {t("showroomAdmin.menuStep1")}</Text>
                  <Text variant="bodyMd"><strong>2.</strong> {t("showroomAdmin.menuStep2")}</Text>
                  <Text variant="bodyMd"><strong>3.</strong> {t("showroomAdmin.menuStep3")}</Text>
                </BlockStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="300">
                {loadingPreview ? (
                  <InlineStack align="center"><Spinner size="small" /></InlineStack>
                ) : previewError ? (
                  <Banner tone="warning">{t("showroomAdmin.loadFailed")}</Banner>
                ) : preview && preview.length > 0 ? (
                  <>
                    <Text variant="headingMd" as="h2">
                      {t("showroomAdmin.productsAvailable", { count: preview.length })}
                    </Text>
                    <InlineStack gap="300" wrap>
                      {preview.slice(0, 12).map((p) => (
                        <BlockStack key={p.productId} gap="100" inlineAlign="center">
                          <Thumbnail
                            source={p.imageUrl || ""}
                            alt={p.title}
                            size="large"
                          />
                          <div style={{ maxWidth: 90, textAlign: "center" }}>
                            <Text variant="bodySm" truncate>{p.title}</Text>
                          </div>
                        </BlockStack>
                      ))}
                    </InlineStack>
                  </>
                ) : (
                  <Banner tone="info">{t("showroomAdmin.noProducts")}</Banner>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
